import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdArrowForward } from 'react-icons/md';
import {
  PRODUCT_CATALOG,
  PRODUCT_ORDER,
  type ProductId,
} from './customizerConfig';
import { THEME } from './theme';

interface ProductGridProps {
  onSelect: (id: ProductId) => void;
}

const SLIDE_INTERVAL = 4000;

/* ── Shimmer skeleton ── */
const ImageSkeleton: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: '14px' }}>
    <div
      className="w-full h-full"
      style={{
        background: `linear-gradient(110deg, 
          rgba(255,255,255,0.03) 0%, 
          rgba(255,255,255,0.07) 40%, 
          rgba(255,255,255,0.03) 60%)`,
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.8s ease-in-out infinite',
      }}
    />
    <style>{`
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

/* ── Compact product card ── */
const ProductCard: React.FC<{
  id: ProductId;
  idx: number;
  onSelect: (id: ProductId) => void;
}> = ({ id, idx, onSelect }) => {
  const product = PRODUCT_CATALOG[id];
  const images = product.baseImages.map((img) => img.path);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, [images.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [nextSlide]);

  useEffect(() => {
    startTimeRef.current = Date.now();
    const animate = () => {
      const p = Math.min((Date.now() - startTimeRef.current) / SLIDE_INTERVAL, 1);
      setProgress(p);
      if (p < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [currentSlide]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 36 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.2, duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
      className="overflow-hidden flex flex-row group mt-10 cursor-pointer"
      style={{
        borderRadius: '20px',
        background: `linear-gradient(135deg, ${THEME.bgCard}, rgba(30, 30, 38, 0.85))`,
        border: `1px solid ${THEME.border}`,
        height: '70vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
        transition: 'border-color 0.4s ease, box-shadow 0.4s ease',
      }}
      whileHover={{
        borderColor: THEME.primaryBorder,
        boxShadow: `0 8px 40px rgba(0,0,0,0.3), ${THEME.primaryGlow}`,
      }}
      onClick={() => onSelect(id)}
    >
      {/* ── LEFT: Info ── */}
      <div className="flex flex-col justify-center px-8 md:px-10 py-8 flex-shrink-0" style={{ width: '42%' }}>

        <motion.h3
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.2 + 0.2, duration: 0.5 }}
          className="font-heading text-[22px] md:text-[26px] font-bold tracking-tight leading-tight mb-3"
          style={{
            color: THEME.text,
          }}
        >
          {product.name}
        </motion.h3>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.2 + 0.3, duration: 0.45 }}
          className="text-[13px] leading-relaxed mb-6 line-clamp-2"
          style={{ color: THEME.textSecondary }}
        >
          {product.tagline}
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.2 + 0.4, duration: 0.45 }}
          whileHover={{ scale: 1.05, boxShadow: THEME.primaryGlowStrong }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => { e.stopPropagation(); onSelect(id); }}
          className="flex items-center gap-2.5 w-fit px-6 py-2.5 rounded-full text-[13px] font-semibold tracking-wide cursor-pointer transition-shadow duration-300"
          style={{
            background: THEME.primary,
            color: '#ffffff',
            boxShadow: THEME.primaryGlow,
          }}
        >
          Customize
          <motion.span
            className="inline-flex"
            animate={{ x: [0, 4, 0] }}
            transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <MdArrowForward size={15} />
          </motion.span>
        </motion.button>
      </div>

      {/* ── RIGHT: Image slideshow ── */}
      <div className="flex-1 flex flex-col p-3">
        <div className="relative flex-1 overflow-hidden" style={{ borderRadius: '12px' }}>
          {!imageLoaded && <ImageSkeleton />}
          <AnimatePresence mode="wait">
            <motion.img
              key={`${id}-${currentSlide}`}
              src={images[currentSlide]}
              alt={`${product.name} – ${currentSlide + 1}`}
              className="absolute inset-0 w-full h-full object-contain"
              style={{ borderRadius: '12px' }}
              draggable={false}
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
              onLoad={() => setImageLoaded(true)}
            />
          </AnimatePresence>
        </div>

        {/* Timeline bars */}
        <div className="flex items-center gap-1.5 mt-2.5 px-0.5">
          {images.map((_, i) => (
            <div
              key={i}
              className="flex-1 h-[2.5px] rounded-full overflow-hidden cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentSlide(i);
                setProgress(0);
                startTimeRef.current = Date.now();
              }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  background: i <= currentSlide ? THEME.primary : 'transparent',
                  width:
                    i < currentSlide
                      ? '100%'
                      : i === currentSlide
                      ? `${progress * 100}%`
                      : '0%',
                  transition: i === currentSlide ? 'none' : 'width 0.3s ease',
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

/* ── Grid: side-by-side in one row ── */
const ProductGrid: React.FC<ProductGridProps> = ({ onSelect }) => {
  return (
    <div className="w-full flex items-start justify-center" style={{ minHeight: 'calc(100vh - 10rem)' }}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full mt-2">
        {PRODUCT_ORDER.map((id, idx) => (
          <ProductCard key={id} id={id} idx={idx} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
};

export default ProductGrid;
